<template>
	<div class="flex flex-row gap-2 justify-between">
		<div v-if="!wishProduct && !quantity">
			<ProductProductAttributesAddToCartQuantitySelector
				v-model="quantityModel"
				:min="1"
				:max="stock"
			/>
		</div>
		<button
			class="tracking-[1.82px] uppercase !text-[13px]"
			:class="[
				'w-full h-full',
				`${!disableButton ? 'cursor-pointer button-black' : 'cursor-not-allowed button-secondary'}`
			]"
			data-testid="add-to-cart-button"
			:disabled="cartStore.updating || disableButton || stock === 0"
			@click="handleAddToCart"
		>
			<div v-if="cartStore.updating">
				<div
					class="inline-block h-4 w-4 mr-4 animate-spin rounded-full border-4 border-solid border-current border-e-transparent align-[-3px] text-surface motion-reduce:animate-[spin_1.5s_linear_infinite] dark:text-white"
					role="status"
				></div>
				<span>{{ $t("Adding") }}..</span>
			</div>
			<span v-else>{{
				stock !== 0 ? $t("Add to Cart") : $t("no_longer_available")
			}}</span>
		</button>
	</div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import type { ProductItem } from "@nwt/norce-pwa/dist/runtime/types/api/product/product";
import { BASKET_ITEM_INFO_CODES } from "~/templates/breadandboxers/utils/basketItemInfoCodes";

interface Props {
	stock: number;
	wishProduct?: ProductItem;
	quantity?: number;
}
const { stock, wishProduct, quantity: quantityProp } = defineProps<Props>();
const emit = defineEmits<{
	(e: "update:quantity", value: number): void;
}>();

const cartStore = useCartStore();
const { addToCart } = useGtmEvents();

const { selectedSecondParametric, selectedColor, selectedProduct } =
	storeToRefs(useProductStore());

const disableButton = computed(() => {
	if (!wishProduct) {
		if (!selectedColor.value && !selectedSecondParametric.value) {
			return stock < 1;
		} else if (selectedColor.value) {
			if (selectedSecondParametric.value) {
				return !selectedSecondParametric.value?.InStock;
			}
			return !selectedColor.value?.InStock;
		}
	}
	return false;
});

const localQuantity = ref(1);

const quantityModel = computed({
	get() {
		return quantityProp ?? localQuantity.value;
	},
	set(value: number) {
		if (quantityProp) {
			emit("update:quantity", value);
		} else {
			localQuantity.value = value;
		}
	}
});

const handleAddToCart = () => {
	const product = wishProduct || selectedProduct.value;

	if (!product) {
		return;
	}

	const config = useRuntimeConfig();
	const basketItemInfoTypes: { TypeId: number; Code: string }[] =
		config.public.norce.basketItemInfoTypes ?? [];

	const itemGroupIdType = basketItemInfoTypes.find(
		(t) => t.Code === BASKET_ITEM_INFO_CODES.GROUP_ID
	);
	const itemColorType = basketItemInfoTypes.find(
		(t) => t.Code === BASKET_ITEM_INFO_CODES.COLOR
	);
	const itemSizeType = basketItemInfoTypes.find(
		(t) => t.Code === BASKET_ITEM_INFO_CODES.SIZE
	);

	const itemColorId =
		"VariantParametrics" in product && product.VariantParametrics != null
			? product.VariantParametrics.find((p) => p.Code === "color")?.Id
			: undefined;
	const itemColor =
		selectedColor.value?.Name ??
		selectedColor.value?.Value ??
		("VariantParametrics" in product && product.VariantParametrics != null
			? product.VariantParametrics.find((p) => p.Code === "color")?.Value
			: undefined);
	const itemSize =
		selectedSecondParametric.value?.Name ??
		selectedSecondParametric.value?.Value ??
		("VariantParametrics" in product && product.VariantParametrics != null
			? product.VariantParametrics.find((p) => p.Code === "size")?.Value
			: undefined);

	const itemGroupId =
		product.GroupByKey != null
			? `${product.GroupByKey}-${itemColorId}`
			: undefined;
	const itemInfo = [
		...(itemGroupIdType && itemGroupId
			? [
					{
						TypeId: itemGroupIdType.TypeId,
						Code: itemGroupIdType.Code,
						Value: itemGroupId
					}
				]
			: []),
		...(itemColorType && itemColor
			? [
					{
						TypeId: itemColorType.TypeId,
						Code: itemColorType.Code,
						Value: itemColor
					}
				]
			: []),
		...(itemSizeType && itemSize
			? [
					{
						TypeId: itemSizeType.TypeId,
						Code: itemSizeType.Code,
						Value: itemSize
					}
				]
			: [])
	];

	// No `PriceListId`. Every basket call already carries the shopper's
	// `pricelistSeed`, and reading the same basket back with a different seed
	// returns different prices — so the seed is what prices a line, and naming
	// one list here only pinned whichever the API happened to return first.
	cartStore.addToCart({
		PartNo: product.PartNo,
		Quantity: quantityModel.value,
		Comment: null,
		Name: wishProduct ? product.Name : product.VariantName || product.Name,
		...(itemInfo.length
			? {
					Info: itemInfo
				}
			: {})
	});
	addToCart(product);
};
</script>
